import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  AlignItems,
  Body1,
  Body2,
  Bold,
  Box,
  Color,
  Display,
  FontFamily,
  Icon,
  JustifyContent,
  Link,
  Space,
  useModal,
} from '@gousto-internal/citrus-react'

import { SubscriptionContext } from '../../../context'
import { actionTypes } from '../../../context/reducers'
import { getDeliveryFrequency } from '../../../context/selectors/deliveries'
import { trackSubscriptionSettingsChange } from '../../../tracking'
import { generateNextDeliveryFrequencyString } from '../../generateNextDeliveryFrequencyString'
import { CalendarDeliverDay } from '../../hooks/useSubscriptionCalendar'
import { DeliveryDays } from '../../hooks/useSubscriptionToDeliveryDays'
import { CalendarModal, SUBSCRIPTION_CALENDAR_MODAL_NAME } from '../../modals/CalendarModal'
import { FrequencyKey } from '../../types'
import { dateFormatter } from '../../utils/dateFormatter'
import { userAgent } from '../../utils/userAgent'

type Props = { subscriptionToDays: DeliveryDays }

export const NextDeliverySection = (props: Props) => {
  const dateFormatterModule = dateFormatter()
  const { subscriptionToDays } = props
  const { state, dispatch } = useContext<any>(SubscriptionContext)
  const isMobile = useMemo(() => userAgent().isMobile(), [])

  const currentDeliveryFrequency = getDeliveryFrequency(state) as FrequencyKey
  const [selectedDeliveryDayFromCalendar, setSelectedDeliveryDayFromCalendar] =
    useState<CalendarDeliverDay | null>(null)
  const { openModal } = useModal()
  const previousSelectedDate = subscriptionToDays.deliveryDate
  const earliestAvailableDelivery = subscriptionToDays.data && subscriptionToDays.data[0]

  const deliveryDateCopy = !selectedDeliveryDayFromCalendar
    ? previousSelectedDate
    : selectedDeliveryDayFromCalendar.date

  const nextDeliveryFrequencyString = generateNextDeliveryFrequencyString(
    currentDeliveryFrequency,
    deliveryDateCopy,
  )

  const trackEarliestDeliveryDate = useCallback(() => {
    trackSubscriptionSettingsChange({ settingName: 'earliest_delivery_date', action: 'display' })({
      delivery_date: earliestAvailableDelivery?.date,
      order_type: 'resubscription',
    })
  }, [earliestAvailableDelivery?.date])

  useEffect(() => {
    if (earliestAvailableDelivery) {
      trackEarliestDeliveryDate()
    }
  }, [earliestAvailableDelivery, trackEarliestDeliveryDate])

  const onNextDeliveryClick = (event: React.SyntheticEvent) => {
    event.preventDefault()
    openModal(SUBSCRIPTION_CALENDAR_MODAL_NAME)
    trackSubscriptionSettingsChange({ settingName: 'reactivate_delivery_date', action: 'edit' })({
      previous_date: previousSelectedDate,
    })
  }

  const updateSelectedDayFromCalendar = (day: CalendarDeliverDay) => {
    setSelectedDeliveryDayFromCalendar(day)
    dispatch({
      type: actionTypes.UPDATE_CALENDAR_DELIVERY_DATE,
      data: { slotId: day.slotId, selectedDate: day.date },
    })
  }

  const renderEarliestAvailableDateNotification = () => {
    const editInfoCopy = isMobile ? 'Tap' : 'Click'

    if (!earliestAvailableDelivery) {
      return null
    }

    const wantADifferentDateCopy = `Want a different date? ${editInfoCopy} edit`
    const formattedDate = dateFormatterModule.getFormattedMonthDayWithWeekday(
      earliestAvailableDelivery.date,
    )

    return (
      <Box
        display={Display.Block}
        bg={Color.Informative_50}
        borderColor={Color.Informative_200}
        width="100%"
        paddingV={3}
        paddingH={3}
        borderRadius={1.5}
        data-testid="earlist-date-info-panel"
      >
        <Body2 color={Color.ColdGrey_800}>
          <Bold>Earliest available:</Bold> {isMobile && formattedDate}
          {!isMobile && `${formattedDate} - ${wantADifferentDateCopy}`}
        </Body2>
        {isMobile && <Body2 color={Color.ColdGrey_800}>{wantADifferentDateCopy}</Body2>}
      </Box>
    )
  }

  return (
    <>
      <CalendarModal
        deliveryDays={subscriptionToDays.data}
        preSelectedDay={previousSelectedDate}
        updateSelectedDayFromCalendar={updateSelectedDayFromCalendar}
        showModal={false}
      />

      <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
        <Box width={['2.5rem']}>
          <Icon name="calendar" />
        </Box>
        <Box width={['100%']}>
          <Body1 fontFamily={FontFamily.Bold} color={Color.ColdGrey_800}>
            Next Delivery
          </Body1>
          <Body2>
            <span>
              <Bold color={Color.ColdGrey_800}>
                {/* TODO: write a test that checks this string is correct in ui */}
                {dateFormatterModule.getFormattedMonthDayWithWeekday(String(deliveryDateCopy))}
              </Bold>{' '}
              {nextDeliveryFrequencyString}
            </span>
          </Body2>
        </Box>
        <Box
          width={['3rem']}
          display={Display.Flex}
          alignItems={AlignItems.Baseline}
          justifyContent={JustifyContent.FlexEnd}
        >
          <Link
            href="#next-delivery"
            onClick={onNextDeliveryClick}
            data-testid="nextDeliveryEditButton"
          >
            Edit
          </Link>
        </Box>
      </Box>
      <Space size={4} direction="vertical" />
      {renderEarliestAvailableDateNotification()}
    </>
  )
}
